/**
 * Hook for fetching the "How your words shaped this" influence description.
 *
 * Generates a warm explanation of how the participant's conversation
 * influenced the generated image.
 *
 * On error, description is set to null — the UI component decides
 * whether to show nothing, display an error, or use a generic message.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { getApiClient } from '@/services/api'

export interface UseInfluenceDescriptionResult {
  description: string | null
  isLoading: boolean
  error: string | null
  fetch: () => Promise<void>
}

export function useInfluenceDescription(
  sessionId: string | null
): UseInfluenceDescriptionResult {
  const [description, setDescription] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const fetchDescription = useCallback(async () => {
    if (!sessionId) return

    // Abort any in-flight request before starting a new one
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    setIsLoading(true)
    setError(null)
    setDescription(null)

    try {
      const client = getApiClient()
      const result = await client.getInfluenceDescription(sessionId, controller.signal)

      // Guard against state updates after abort/unmount
      if (controller.signal.aborted) return

      setDescription(result.description)
    } catch (err) {
      // Silently ignore aborted requests (component unmounted or new request started)
      if (controller.signal.aborted) return

      console.error('Failed to fetch influence description:', err)
      // Set error for the UI to handle — no deceptive fallback text
      setError('Could not generate influence description')
      setDescription(null)
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false)
      }
    }
  }, [sessionId])

  // Abort any in-flight request on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  return { description, isLoading, error, fetch: fetchDescription }
}
